'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface A11ySettings {
  fontSize: number // 0 = normal, 1 = large, 2 = x-large
  highContrast: boolean
  colorFilter: '' | 'protanopia' | 'deuteranopia' | 'tritanopia'
  dyslexiaFont: boolean
  largeCursor: boolean
  highlightLinks: boolean
  textSpacing: boolean
  stopAnimations: boolean
}

const DEFAULT_SETTINGS: A11ySettings = {
  fontSize: 0,
  highContrast: false,
  colorFilter: '',
  dyslexiaFont: false,
  largeCursor: false,
  highlightLinks: false,
  textSpacing: false,
  stopAnimations: false,
}

const STORAGE_KEY = 'taskyinn-a11y'

// ---------------------------------------------------------------------------
// Icons (inline SVG to avoid dependency)
// ---------------------------------------------------------------------------
function AccessibilityIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="4.5" r="2" />
      <path d="M7 8h10" />
      <path d="M12 8v5" />
      <path d="M9 20l3-7 3 7" />
    </svg>
  )
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className={className}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Feature definitions
// ---------------------------------------------------------------------------
interface Feature {
  id: keyof A11ySettings
  label_en: string
  label_ar: string
  icon: React.ReactNode
  type: 'toggle' | 'stepper' | 'select'
}

const FONT_SIZE_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5">
    <path d="M4 7V4h16v3" /><path d="M9 20h6" /><path d="M12 4v16" />
  </svg>
)

const CONTRAST_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-5 w-5">
    <circle cx="12" cy="12" r="9" /><path d="M12 3v18" /><path d="M12 3a9 9 0 0 1 0 18" fill="currentColor" opacity="0.3" />
  </svg>
)

const EYE_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
  </svg>
)

const FONT_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5">
    <path d="M6 20h12" /><path d="M9 4h6" /><path d="M12 4v16" /><path d="M4 12h4" /><path d="M16 12h4" />
  </svg>
)

const CURSOR_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5">
    <path d="M5 3l14 7-6 2-2 6z" /><path d="M14 14l4 4" />
  </svg>
)

const LINK_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
)

const SPACING_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5">
    <path d="M8 7h8" /><path d="M8 12h8" /><path d="M8 17h8" /><path d="M4 4v16" /><path d="M20 4v16" />
  </svg>
)

const MOTION_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5">
    <circle cx="12" cy="12" r="9" /><rect x="10" y="8" width="4" height="8" rx="0.5" fill="currentColor" opacity="0.4" />
  </svg>
)

const FEATURES: Feature[] = [
  { id: 'fontSize', label_en: 'Font Size', label_ar: 'حجم الخط', icon: FONT_SIZE_ICON, type: 'stepper' },
  { id: 'highContrast', label_en: 'High Contrast', label_ar: 'تباين عالي', icon: CONTRAST_ICON, type: 'toggle' },
  { id: 'colorFilter', label_en: 'Color Blind', label_ar: 'عمى الألوان', icon: EYE_ICON, type: 'select' },
  { id: 'dyslexiaFont', label_en: 'Dyslexia Font', label_ar: 'خط عسر القراءة', icon: FONT_ICON, type: 'toggle' },
  { id: 'largeCursor', label_en: 'Large Cursor', label_ar: 'مؤشر كبير', icon: CURSOR_ICON, type: 'toggle' },
  { id: 'highlightLinks', label_en: 'Highlight Links', label_ar: 'تمييز الروابط', icon: LINK_ICON, type: 'toggle' },
  { id: 'textSpacing', label_en: 'Text Spacing', label_ar: 'تباعد النص', icon: SPACING_ICON, type: 'toggle' },
  { id: 'stopAnimations', label_en: 'Stop Animations', label_ar: 'إيقاف الحركة', icon: MOTION_ICON, type: 'toggle' },
]

const COLOR_FILTERS = [
  { value: '', label_en: 'None', label_ar: 'بدون' },
  { value: 'protanopia', label_en: 'Protanopia', label_ar: 'بروتانوبيا' },
  { value: 'deuteranopia', label_en: 'Deuteranopia', label_ar: 'ديوتيرانوبيا' },
  { value: 'tritanopia', label_en: 'Tritanopia', label_ar: 'تريتانوبيا' },
] as const

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function AccessibilityWidget() {
  const [open, setOpen] = useState(false)
  const [settings, setSettings] = useState<A11ySettings>(DEFAULT_SETTINGS)
  const [mounted, setMounted] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Detect locale from html dir attribute
  const [isRtl, setIsRtl] = useState(false)
  useEffect(() => {
    setIsRtl(document.documentElement.dir === 'rtl')
  }, [])

  // Load settings from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        setSettings({ ...DEFAULT_SETTINGS, ...JSON.parse(saved) })
      }
    } catch {}
    setMounted(true)
  }, [])

  // Persist settings
  useEffect(() => {
    if (!mounted) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch {}
  }, [settings, mounted])

  // Apply settings to document
  useEffect(() => {
    if (!mounted) return
    const root = document.documentElement
    const body = document.body

    // Font size
    root.classList.remove('a11y-font-1', 'a11y-font-2')
    if (settings.fontSize === 1) root.classList.add('a11y-font-1')
    if (settings.fontSize === 2) root.classList.add('a11y-font-2')

    // High contrast
    root.classList.toggle('a11y-high-contrast', settings.highContrast)

    // Color blind filter
    root.classList.remove('a11y-protanopia', 'a11y-deuteranopia', 'a11y-tritanopia')
    if (settings.colorFilter) root.classList.add(`a11y-${settings.colorFilter}`)

    // Dyslexia font
    root.classList.toggle('a11y-dyslexia', settings.dyslexiaFont)

    // Large cursor
    body.classList.toggle('a11y-large-cursor', settings.largeCursor)

    // Highlight links
    root.classList.toggle('a11y-highlight-links', settings.highlightLinks)

    // Text spacing
    root.classList.toggle('a11y-text-spacing', settings.textSpacing)

    // Stop animations
    root.classList.toggle('a11y-stop-animations', settings.stopAnimations)
  }, [settings, mounted])

  // Close on click outside
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  const update = useCallback(<K extends keyof A11ySettings>(key: K, value: A11ySettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }, [])

  const resetAll = useCallback(() => {
    setSettings(DEFAULT_SETTINGS)
  }, [])

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(DEFAULT_SETTINGS)
  const activeCount = [
    settings.fontSize > 0,
    settings.highContrast,
    settings.colorFilter !== '',
    settings.dyslexiaFont,
    settings.largeCursor,
    settings.highlightLinks,
    settings.textSpacing,
    settings.stopAnimations,
  ].filter(Boolean).length

  if (!mounted) return null

  return (
    <>
      {/* SVG Filters (hidden, referenced by CSS) */}
      <svg className="absolute h-0 w-0" aria-hidden="true">
        <defs>
          <filter id="a11y-protanopia">
            <feColorMatrix type="matrix" values="0.567,0.433,0,0,0 0.558,0.442,0,0,0 0,0.242,0.758,0,0 0,0,0,1,0" />
          </filter>
          <filter id="a11y-deuteranopia">
            <feColorMatrix type="matrix" values="0.625,0.375,0,0,0 0.7,0.3,0,0,0 0,0.3,0.7,0,0 0,0,0,1,0" />
          </filter>
          <filter id="a11y-tritanopia">
            <feColorMatrix type="matrix" values="0.95,0.05,0,0,0 0,0.433,0.567,0,0 0,0.475,0.525,0,0 0,0,0,1,0" />
          </filter>
        </defs>
      </svg>

      {/* Floating trigger button */}
      <button
        ref={buttonRef}
        onClick={() => setOpen(prev => !prev)}
        aria-label="Accessibility settings"
        aria-expanded={open}
        className={cn(
          'fixed z-40 flex items-center justify-center rounded-full shadow-lg transition-all duration-300',
          'h-12 w-12 sm:h-14 sm:w-14',
          'bottom-20 lg:bottom-6',
          isRtl ? 'right-4 sm:right-6' : 'left-4 sm:left-6',
          open
            ? 'bg-gray-800 text-white dark:bg-white dark:text-gray-900 scale-90'
            : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-200 hover:scale-105 hover:shadow-xl',
          'border border-gray-200 dark:border-gray-700',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        )}
      >
        {open ? (
          <CloseIcon className="h-5 w-5 sm:h-6 sm:w-6" />
        ) : (
          <AccessibilityIcon className="h-6 w-6 sm:h-7 sm:w-7" />
        )}
        {/* Active count badge */}
        {!open && activeCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white shadow">
            {activeCount}
          </span>
        )}
      </button>

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Accessibility settings"
        className={cn(
          'fixed z-50 transition-all duration-300 ease-out',
          'bottom-[136px] lg:bottom-[88px]',
          isRtl ? 'right-4 sm:right-6' : 'left-4 sm:left-6',
          'w-[320px] sm:w-[340px]',
          open
            ? 'translate-y-0 opacity-100 pointer-events-auto'
            : 'translate-y-4 opacity-0 pointer-events-none',
        )}
      >
        <div className="rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30">
                <AccessibilityIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {isRtl ? 'إمكانية الوصول' : 'Accessibility'}
                </h2>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {isRtl ? 'خصص تجربة التصفح' : 'Customize your experience'}
                </p>
              </div>
            </div>
            {hasChanges && (
              <button
                onClick={resetAll}
                className="rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
              >
                {isRtl ? 'إعادة تعيين' : 'Reset'}
              </button>
            )}
          </div>

          {/* Feature list */}
          <div className="max-h-[60vh] overflow-y-auto overscroll-contain px-3 py-2">
            {FEATURES.map((feature) => {
              const label = isRtl ? feature.label_ar : feature.label_en

              return (
                <div
                  key={feature.id}
                  className="flex items-center justify-between gap-3 rounded-xl px-2 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      {feature.icon}
                    </div>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                      {label}
                    </span>
                  </div>

                  {/* Controls */}
                  {feature.type === 'toggle' && (
                    <ToggleSwitch
                      checked={!!settings[feature.id]}
                      onChange={(v) => setSettings(prev => ({ ...prev, [feature.id]: v }))}
                      label={label}
                    />
                  )}

                  {feature.type === 'stepper' && (
                    <div className="flex items-center gap-1">
                      <StepperButton
                        label="A-"
                        disabled={settings.fontSize <= 0}
                        onClick={() => update('fontSize', Math.max(0, settings.fontSize - 1))}
                        small
                      />
                      <span className="w-6 text-center text-xs font-semibold text-gray-600 dark:text-gray-300">
                        {settings.fontSize === 0 ? 'A' : settings.fontSize === 1 ? 'A+' : 'A++'}
                      </span>
                      <StepperButton
                        label="A+"
                        disabled={settings.fontSize >= 2}
                        onClick={() => update('fontSize', Math.min(2, settings.fontSize + 1))}
                      />
                    </div>
                  )}

                  {feature.type === 'select' && (
                    <select
                      value={settings.colorFilter}
                      onChange={(e) => update('colorFilter', e.target.value as A11ySettings['colorFilter'])}
                      aria-label={label}
                      className="h-8 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                      {COLOR_FILTERS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {isRtl ? opt.label_ar : opt.label_en}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )
            })}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 dark:border-gray-800 px-5 py-3">
            <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center leading-relaxed">
              {isRtl
                ? 'يتم حفظ إعداداتك تلقائيًا في هذا المتصفح'
                : 'Your settings are saved automatically in this browser'}
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ToggleSwitch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        checked ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200',
          checked ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </button>
  )
}

function StepperButton({
  label,
  disabled,
  onClick,
  small,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  small?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 font-semibold transition-colors dark:border-gray-700 dark:bg-gray-800',
        'hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        'h-7 w-7 text-xs text-gray-700 dark:text-gray-300',
        small && 'text-[10px]',
      )}
    >
      {label}
    </button>
  )
}
